import {
  HabilitationActionCode,
  HabilitationBlockingReasonCode,
  HabilitationDocumentCode,
  HabilitationStepCode,
} from '@graphql/generated';
import { HabilitationType } from './types/habilitation-type.type';
import {
  ActionParcoursHabilitation,
  CodeDocumentHabilitation,
  CodeEtapeParcoursHabilitation,
  HabilitationParcours,
  HabilitationParcoursEtape,
  MiseAJourParcours,
} from './habilitation-parcours.model';

export interface HabilitationStepPresentation {
  code: CodeEtapeParcoursHabilitation;
  renderer: 'checklist' | 'stefi';
  title: string;
  titleShort: string;
}

const COMMON_STEP_PRESENTATION: Record<
  HabilitationStepCode,
  HabilitationStepPresentation
> = {
  [HabilitationStepCode.Pieces]: {
    code: 'pieces',
    renderer: 'checklist',
    title: 'Dépôt des pièces',
    titleShort: 'Pièces',
  },
  [HabilitationStepCode.CollaborationStefi]: {
    code: 'collaboration_stefi',
    renderer: 'stefi',
    title: 'Collaboration Stellium Financement',
    titleShort: 'Collaboration SteFi',
  },
  [HabilitationStepCode.DemandeOrias]: {
    code: 'demande_orias',
    renderer: 'checklist',
    title: "Demande d'inscription ORIAS",
    titleShort: 'Demande ORIAS',
  },
  [HabilitationStepCode.FinalisationOrias]: {
    code: 'finalisation_orias',
    renderer: 'checklist',
    title: "Finalisation de l'inscription ORIAS",
    titleShort: 'Finalisation ORIAS',
  },
  [HabilitationStepCode.ControlePiecesShm]: {
    code: 'controle_pieces_shm',
    renderer: 'checklist',
    title: 'Contrôle des pièces',
    titleShort: 'Contrôle',
  },
  [HabilitationStepCode.DeclarationMandatOriasShm]: {
    code: 'declaration_mandat_orias_shm',
    renderer: 'checklist',
    title: "Déclaration du mandat à l'ORIAS",
    titleShort: 'Mandat ORIAS',
  },
  [HabilitationStepCode.RetourOriasShm]: {
    code: 'retour_orias_shm',
    renderer: 'checklist',
    title: 'Retour ORIAS',
    titleShort: 'Retour ORIAS',
  },
  [HabilitationStepCode.FormationImmo]: {
    code: 'formation_immo',
    renderer: 'checklist',
    title: 'Formation immobilier',
    titleShort: 'Formation',
  },
  [HabilitationStepCode.FormationHabilitanteAlpsi]: {
    code: 'formation_habilitante_alpsi',
    renderer: 'checklist',
    title: 'Formation habilitante ALPSI',
    titleShort: 'Formation',
  },
  [HabilitationStepCode.FormationHabilitanteAssurance]: {
    code: 'formation_habilitante_assurance',
    renderer: 'checklist',
    title: 'Formation habilitante assurance',
    titleShort: 'Formation',
  },
  [HabilitationStepCode.FormationHabilitanteFinancement]: {
    code: 'formation_habilitante_financement',
    renderer: 'checklist',
    title: 'Formation habilitante financement',
    titleShort: 'Formation',
  },
  [HabilitationStepCode.FormationHabilitanteCif]: {
    code: 'formation_habilitante_cif',
    renderer: 'checklist',
    title: 'Formation habilitante CIF',
    titleShort: 'Formation',
  },
  [HabilitationStepCode.CotisationCci]: {
    code: 'cotisation_cci',
    renderer: 'checklist',
    title: 'Paiement de la cotisation CCI',
    titleShort: 'Cotisation CCI',
  },
  [HabilitationStepCode.ControleFormationImmoShm]: {
    code: 'controle_formation_immo_shm',
    renderer: 'checklist',
    title: 'Contrôle de la formation',
    titleShort: 'Contrôle formation',
  },
  [HabilitationStepCode.GenerationCerfaCciShm]: {
    code: 'generation_cerfa_cci_shm',
    renderer: 'checklist',
    title: 'Génération du Cerfa CCI',
    titleShort: 'Cerfa CCI',
  },
  [HabilitationStepCode.ConstitutionDossierCciShm]: {
    code: 'constitution_dossier_cci_shm',
    renderer: 'checklist',
    title: 'Constitution du dossier CCI',
    titleShort: 'Dossier CCI',
  },
  [HabilitationStepCode.RetourCciShm]: {
    code: 'retour_cci_shm',
    renderer: 'checklist',
    title: 'Retour de la CCI',
    titleShort: 'Retour CCI',
  },
  [HabilitationStepCode.AttestationCollaborateurShm]: {
    code: 'attestation_collaborateur_shm',
    renderer: 'checklist',
    title: 'Attestation collaborateur',
    titleShort: 'Attestation collaborateur',
  },
  [HabilitationStepCode.ActivationHabilitationImmoShm]: {
    code: 'activation_habilitation_immo_shm',
    renderer: 'checklist',
    title: "Activation de l'habilitation immobilier",
    titleShort: 'Activation',
  },
  [HabilitationStepCode.AdhesionAssociationCif]: {
    code: 'adhesion_association_cif',
    renderer: 'checklist',
    title: "Adhésion à l'association professionnelle CIF",
    titleShort: 'Adhésion CIF',
  },
  [HabilitationStepCode.DemandeSuppressionMandatAlpsiCif]: {
    code: 'demande_suppression_mandat_alpsi_cif',
    renderer: 'checklist',
    title: 'Demande de suppression du mandat ALPSI',
    titleShort: 'Demande suppression ALPSI',
  },
  [HabilitationStepCode.SuppressionMandatAlpsiOriasShm]: {
    code: 'suppression_mandat_alpsi_orias_shm',
    renderer: 'checklist',
    title: 'Demande de suppression du mandat ALPSI auprès de l’ORIAS',
    titleShort: 'Suppression ORIAS',
  },
  [HabilitationStepCode.ConfirmationSuppressionMandatAlpsiShm]: {
    code: 'confirmation_suppression_mandat_alpsi_shm',
    renderer: 'checklist',
    title: 'Confirmation de la suppression du mandat ALPSI',
    titleShort: 'Confirmation suppression',
  },
  [HabilitationStepCode.InscriptionOriasCif]: {
    code: 'inscription_orias_cif',
    renderer: 'checklist',
    title: 'Inscription ORIAS en qualité de CIF',
    titleShort: 'Inscription ORIAS CIF',
  },
  [HabilitationStepCode.RetourOriasCif]: {
    code: 'retour_orias_cif',
    renderer: 'checklist',
    title: 'Retour ORIAS CIF',
    titleShort: 'Retour ORIAS CIF',
  },
  [HabilitationStepCode.ConventionDistributionCif]: {
    code: 'convention_distribution_cif',
    renderer: 'checklist',
    title: 'Convention de distribution CIF',
    titleShort: 'Convention CIF',
  },
  [HabilitationStepCode.EnvoiSignatureConventionCifShm]: {
    code: 'envoi_signature_convention_cif_shm',
    renderer: 'checklist',
    title: 'Envoi de la convention CIF en signature',
    titleShort: 'Envoi en signature',
  },
  [HabilitationStepCode.SignatureConventionCif]: {
    code: 'signature_convention_cif',
    renderer: 'checklist',
    title: 'Signature de la convention CIF',
    titleShort: 'Signature convention',
  },
  [HabilitationStepCode.EnvoiSignatureMandatAlpsiShm]: {
    code: 'envoi_signature_mandat_alpsi_shm',
    renderer: 'checklist',
    title: 'Envoi du mandat ALPSI en signature',
    titleShort: 'Envoi en signature',
  },
  [HabilitationStepCode.SignatureMandatAlpsi]: {
    code: 'signature_mandat_alpsi',
    renderer: 'checklist',
    title: 'Signature du mandat ALPSI',
    titleShort: 'Signature du mandat',
  },
  [HabilitationStepCode.DeclarationMandantOriasAlpsiShm]: {
    code: 'declaration_mandant_orias_alpsi_shm',
    renderer: 'checklist',
    title: 'Déclaration du mandant à l’ORIAS',
    titleShort: 'Déclaration ORIAS',
  },
  [HabilitationStepCode.FinalisationDossierOriasAlpsi]: {
    code: 'finalisation_dossier_orias_alpsi',
    renderer: 'checklist',
    title: 'Finalisation du dossier ORIAS',
    titleShort: 'Dossier ORIAS',
  },
  [HabilitationStepCode.GenerationCarteDemarchageFinancementShm]: {
    code: 'generation_carte_demarchage_financement_shm',
    renderer: 'checklist',
    title: 'Génération de la carte de démarchage Financement',
    titleShort: 'Carte de démarchage',
  },
  [HabilitationStepCode.GenerationCarteDemarchageAlpsiShm]: {
    code: 'generation_carte_demarchage_alpsi_shm',
    renderer: 'checklist',
    title: 'Génération de la carte de démarchage ALPSI',
    titleShort: 'Carte de démarchage',
  },
};

const STEP_OVERRIDES: Partial<
  Record<
    HabilitationType,
    Partial<Record<HabilitationStepCode, Partial<HabilitationStepPresentation>>>
  >
> = {
  assurance: {
    [HabilitationStepCode.DemandeOrias]: {
      title: 'Demande inscription Orias',
      titleShort: 'Demande inscription Orias',
    },
    [HabilitationStepCode.FinalisationOrias]: {
      title: 'Finalisation inscription Orias',
      titleShort: 'Finalisation inscription Orias',
    },
    [HabilitationStepCode.RetourOriasShm]: {
      title: 'Finalisation inscription Orias du consultant',
      titleShort: 'Finalisation inscription Orias',
    },
  },
  financement: {
    [HabilitationStepCode.DemandeOrias]: {
      title: "Demande d'inscription ORIAS catégorie IOB",
    },
    [HabilitationStepCode.RetourOriasShm]: {
      title: 'Commission ORIAS',
      titleShort: 'Commission ORIAS',
    },
  },
};

/**
 * Étapes conservées dans le parcours (prérequis, synchronisation) mais masquées
 * à l'affichage pour un type d'habilitation donné.
 */
const HIDDEN_STEPS: Partial<Record<HabilitationType, readonly HabilitationStepCode[]>> = {
  assurance: [HabilitationStepCode.RetourOriasShm],
  financement: [HabilitationStepCode.RetourOriasShm],
};

export function isHabilitationStepHidden(
  type: HabilitationType,
  code: HabilitationStepCode
): boolean {
  return HIDDEN_STEPS[type]?.includes(code) ?? false;
}

export {
  getHabilitationCurrentStepLabel,
  getHabilitationCurrentStepPresentation,
  type HabilitationCurrentStepPresentation,
} from './habilitation-current-step-status-presentation';
export {
  getHabilitationStatusPresentation,
  type HabilitationStatusPresentation,
} from './habilitation-status-presentation';

export const HABILITATION_ACTION_PRESENTATION: Record<
  HabilitationActionCode,
  {
    code: ActionParcoursHabilitation;
    label: string;
    buttonLabel?: string;
    url?: string;
  }
> = {
  [HabilitationActionCode.DeposerDocument]: {
    code: 'deposer_document',
    label: 'Déposer',
  },
  [HabilitationActionCode.GenererContratStefi]: {
    code: 'generer_contrat_stefi',
    label: 'Générer le contrat SteFi',
  },
  [HabilitationActionCode.DemanderInscriptionOrias]: {
    code: 'demander_inscription_orias',
    label:
      "Je souhaite être mandaté pour le compte de Stellium Financement auprès de l'Orias",
  },
  [HabilitationActionCode.FinaliserInscriptionOrias]: {
    code: 'finaliser_inscription_orias',
    label: "J'ai finalisé sur le site de l'Orias mon inscription",
  },
  [HabilitationActionCode.DeclarerMandatOrias]: {
    code: 'declarer_mandat_orias',
    label: 'Confirmer la déclaration du mandat',
  },
  [HabilitationActionCode.ConfirmerDeclarationOrias]: {
    code: 'confirmer_declaration_orias',
    label: "Confirmer l'habilitation",
  },
  [HabilitationActionCode.ConfirmerFinalisationOriasBackoffice]: {
    code: 'confirmer_finalisation_orias_backoffice',
    label: "Confirmer la finalisation de l'inscription ORIAS",
  },
  [HabilitationActionCode.CommencerFormationImmo]: {
    code: 'commencer_formation_immo',
    label: 'Commencer la formation',
  },
  [HabilitationActionCode.TerminerFormationImmo]: {
    code: 'terminer_formation_immo',
    label: 'Formation validée par PBS',
  },
  [HabilitationActionCode.CommencerFormationHabilitanteAlpsi]: {
    code: 'commencer_formation_habilitante_alpsi',
    label: 'Commencer la formation',
  },
  [HabilitationActionCode.TerminerFormationHabilitanteAlpsi]: {
    code: 'terminer_formation_habilitante_alpsi',
    label: 'Formation habilitante validée',
  },
  [HabilitationActionCode.CommencerFormationHabilitanteAssurance]: {
    code: 'commencer_formation_habilitante_assurance',
    label: 'Commencer la formation',
  },
  [HabilitationActionCode.TerminerFormationHabilitanteAssurance]: {
    code: 'terminer_formation_habilitante_assurance',
    label: 'Formation habilitante validée',
  },
  [HabilitationActionCode.CommencerFormationHabilitanteFinancement]: {
    code: 'commencer_formation_habilitante_financement',
    label: 'Commencer la formation',
  },
  [HabilitationActionCode.TerminerFormationHabilitanteFinancement]: {
    code: 'terminer_formation_habilitante_financement',
    label: 'Formation habilitante validée',
  },
  [HabilitationActionCode.CommencerFormationHabilitanteCif]: {
    code: 'commencer_formation_habilitante_cif',
    label: 'Commencer la formation',
  },
  [HabilitationActionCode.TerminerFormationHabilitanteCif]: {
    code: 'terminer_formation_habilitante_cif',
    label: 'Formation habilitante validée',
  },
  [HabilitationActionCode.ConfirmerPaiementCotisationCci]: {
    code: 'confirmer_paiement_cotisation_cci',
    label: 'Je confirme avoir effectué le règlement des frais CCI',
  },
  [HabilitationActionCode.ConfirmerGenerationCerfaCci]: {
    code: 'confirmer_generation_cerfa_cci',
    label: 'Générer le Cerfa CCI',
  },
  [HabilitationActionCode.ConfirmerConstitutionDossierCci]: {
    code: 'confirmer_constitution_dossier_cci',
    label: "Confirmer l'envoi du dossier à la CCI",
  },
  [HabilitationActionCode.ConfirmerRetourCci]: {
    code: 'confirmer_retour_cci',
    label: "Confirmer la délivrance de l'attestation de collaborateur",
  },
  [HabilitationActionCode.ConfirmerInformationsAttestationCollaborateur]: {
    code: 'confirmer_informations_attestation_collaborateur',
    label: "Confirmer le renseignement de l'attestation collaborateur",
  },
  [HabilitationActionCode.ConfirmerRecuperationAttestationCollaborateur]: {
    code: 'confirmer_recuperation_attestation_collaborateur',
    label: "Confirmer la récupération de l'attestation collaborateur",
  },
  [HabilitationActionCode.GenererAttestationCollaborateur]: {
    code: 'generer_attestation_collaborateur',
    label: "Générer l'attestation collaborateur",
  },
  [HabilitationActionCode.DeposerAttestationCollaborateur]: {
    code: 'deposer_attestation_collaborateur',
    label: "Déposer l'attestation collaborateur",
  },
  [HabilitationActionCode.TransmettreAttestationCollaborateur]: {
    code: 'transmettre_attestation_collaborateur',
    label: "Transmettre l'attestation collaborateur",
  },
  [HabilitationActionCode.DemanderAdhesionAssociationCif]: {
    code: 'demander_adhesion_association_cif',
    label: "Déclarer la demande d'adhésion à l'association CIF",
  },
  [HabilitationActionCode.ConfirmerAdhesionAssociationCif]: {
    code: 'confirmer_adhesion_association_cif',
    label: "Confirmer la validation de l'adhésion CIF",
  },
  [HabilitationActionCode.DemanderSuppressionMandatAlpsiCif]: {
    code: 'demander_suppression_mandat_alpsi_cif',
    label: 'Demander la suppression du mandat ALPSI',
  },
  [HabilitationActionCode.DeclarerDemandeSuppressionMandatAlpsiOrias]: {
    code: 'declarer_demande_suppression_mandat_alpsi_orias',
    label: 'Confirmer la demande de suppression auprès de l’ORIAS',
  },
  [HabilitationActionCode.ConfirmerNotificationPriseEnCompteSuppressionAlpsi]: {
    code: 'confirmer_notification_prise_en_compte_suppression_alpsi',
    label: 'Informer le consultant de la prise en compte de sa demande',
    buttonLabel: 'Informer',
  },
  [HabilitationActionCode.ConfirmerSuppressionMandatAlpsi]: {
    code: 'confirmer_suppression_mandat_alpsi',
    label: 'Confirmer la suppression effective du mandat ALPSI',
  },
  [HabilitationActionCode.ConfirmerNotificationSuppressionMandatAlpsi]: {
    code: 'confirmer_notification_suppression_mandat_alpsi',
    label: 'Informer le consultant de la suppression effective',
    buttonLabel: 'Informer',
  },
  [HabilitationActionCode.DeclarerInscriptionOriasCif]: {
    code: 'declarer_inscription_orias_cif',
    label: 'Déclarer l’inscription ORIAS CIF',
  },
  [HabilitationActionCode.DeclarerRetourOriasCif]: {
    code: 'declarer_retour_orias_cif',
    label: 'Déclarer le retour ORIAS CIF',
  },
  [HabilitationActionCode.GenererConventionDistributionCif]: {
    code: 'generer_convention_distribution_cif',
    label: 'Générer la convention CIF',
  },
  [HabilitationActionCode.VisualiserConventionDistributionCif]: {
    code: 'visualiser_convention_distribution_cif',
    label: 'Visualiser la convention CIF',
  },
  [HabilitationActionCode.TransmettreConventionDistributionCif]: {
    code: 'transmettre_convention_distribution_cif',
    label: 'Transmettre la convention CIF',
  },
  [HabilitationActionCode.ControlerConventionDistributionCif]: {
    code: 'controler_convention_distribution_cif',
    label: 'Contrôle de la convention CIF',
  },
  [HabilitationActionCode.EnvoyerConventionDistributionCifEnSignature]: {
    code: 'envoyer_convention_distribution_cif_en_signature',
    label: 'Envoyer la convention CIF en signature',
  },
  [HabilitationActionCode.ConfirmerSignatureConventionDistributionCif]: {
    code: 'confirmer_signature_convention_distribution_cif',
    label: 'Signature de la convention CIF',
  },
  [HabilitationActionCode.GenererMandatAlpsi]: {
    code: 'generer_mandat_alpsi',
    label: 'Générer le mandat ALPSI',
  },
  [HabilitationActionCode.ConfirmerEnvoiSignatureMandatAlpsi]: {
    code: 'confirmer_envoi_signature_mandat_alpsi',
    label: 'Envoyer le mandat en signature',
  },
  [HabilitationActionCode.ConfirmerSignatureMandatAlpsi]: {
    code: 'confirmer_signature_mandat_alpsi',
    label: 'Signature du mandat ALPSI',
  },
  [HabilitationActionCode.ConfirmerDeclarationMandantOriasAlpsi]: {
    code: 'confirmer_declaration_mandant_orias_alpsi',
    label: 'Confirmer la déclaration du mandant à l’ORIAS',
  },
  [HabilitationActionCode.ConfirmerFinalisationDossierOriasAlpsi]: {
    code: 'confirmer_finalisation_dossier_orias_alpsi',
    label: 'Confirmer la finalisation du dossier ORIAS',
  },
  [HabilitationActionCode.ConfirmerGenerationCarteDemarchageFinancement]: {
    code: 'confirmer_generation_carte_demarchage_financement',
    label: 'Confirmer la génération de la carte de démarchage Financement',
  },
  [HabilitationActionCode.ConfirmerGenerationCarteDemarchageAlpsi]: {
    code: 'confirmer_generation_carte_demarchage_alpsi',
    label: 'Confirmer la génération de la carte de démarchage',
  },
};
export const HABILITATION_DOCUMENT_PRESENTATION: Record<
  HabilitationDocumentCode,
  { code: CodeDocumentHabilitation; label: string }
> = {
  [HabilitationDocumentCode.DiplomeMaster2]: {
    code: 'diplome_master_2',
    label: 'Diplôme',
  },
  [HabilitationDocumentCode.EquivalencePbs]: {
    code: 'equivalence_pbs',
    label: 'Expérience / livret hors PBS',
  },
  [HabilitationDocumentCode.AttestationEquivalenceFormationCif]: {
    code: 'attestation_equivalence_formation_cif',
    label: 'Expérience / livret hors PBS',
  },
  [HabilitationDocumentCode.AttestationEquivalenceFormationAlpsi]: {
    code: 'attestation_equivalence_formation_alpsi',
    label: 'Expérience / livret hors PBS',
  },
  [HabilitationDocumentCode.Rcp]: {
    code: 'rcp',
    label: 'Responsabilité Civile Professionnelle',
  },
  [HabilitationDocumentCode.Rsac]: {
    code: 'rsac',
    label: "Extrait d'immatriculation RSAC",
  },
  [HabilitationDocumentCode.KbisProfessionLiberale]: {
    code: 'kbis_pl',
    label: 'Extrait KBIS profession libérale',
  },
  [HabilitationDocumentCode.KbisSociete]: {
    code: 'kbis_soc',
    label: 'Extrait KBIS société',
  },
  [HabilitationDocumentCode.Rib]: {
    code: 'rib',
    label: 'RIB',
  },
  [HabilitationDocumentCode.CarteT]: {
    code: 'carte_t',
    label: 'Carte professionnelle T',
  },
  [HabilitationDocumentCode.Cni]: {
    code: 'cni',
    label: "Carte nationale d'identité",
  },
  [HabilitationDocumentCode.PhotoIdentite]: {
    code: 'photo_identite',
    label: "Photo d'identité",
  },
  [HabilitationDocumentCode.Passeport]: {
    code: 'passeport',
    label: 'Passeport',
  },
  [HabilitationDocumentCode.CasierJudiciaire]: {
    code: 'casier_judiciaire',
    label: 'Extrait de casier judiciaire',
  },
  [HabilitationDocumentCode.LettreConsentementCci]: {
    code: 'lettre_consentement_cci',
    label: 'Lettre de consentement CCI',
  },
};

const DOCUMENT_LABEL_OVERRIDES: Partial<
  Record<HabilitationType, Partial<Record<CodeDocumentHabilitation, string>>>
> = {
  financement: {
    diplome_master_2: 'Master Gestion de patrimoine < 2 ans',
    equivalence_pbs: 'Expérience / livret hors PBS',
  },
  alpsi: {
    casier_judiciaire: 'Casier judiciaire B3 de moins de trois mois',
  },
  immobilier: {
    casier_judiciaire:
      'Casier judiciaire du pays de naissance de moins de trois mois, traduit en français par une personne habilitée',
  },
};

export function getHabilitationDocumentPresentation(
  type: HabilitationType,
  code: CodeDocumentHabilitation
): { code: CodeDocumentHabilitation; label: string } | undefined {
  const presentation = Object.values(HABILITATION_DOCUMENT_PRESENTATION).find(
    candidate => candidate.code === code
  );
  if (!presentation) return undefined;

  return {
    ...presentation,
    label: DOCUMENT_LABEL_OVERRIDES[type]?.[code] ?? presentation.label,
  };
}

export const HABILITATION_BLOCKING_REASON_LABELS: Record<
  HabilitationBlockingReasonCode,
  string
> = {
  [HabilitationBlockingReasonCode.DocumentsRequisNonDeposes]:
    'Toutes les pièces requises doivent être déposées.',
  [HabilitationBlockingReasonCode.DocumentsRequisNonConformes]:
    'Toutes les pièces requises doivent être conformes.',
  [HabilitationBlockingReasonCode.ContratStefiNonSigne]:
    'Le contrat Stellium Financement doit être signé.',
  [HabilitationBlockingReasonCode.DemandeOriasNonRealisee]:
    "La demande d'inscription ORIAS doit être réalisée.",
  [HabilitationBlockingReasonCode.MandatOriasNonDeclare]:
    'Le mandat ORIAS doit être déclaré.',
  [HabilitationBlockingReasonCode.InscriptionOriasNonFinalisee]:
    "L'inscription ORIAS doit être finalisée.",
  [HabilitationBlockingReasonCode.CollaborationStefiNonFinalisee]:
    'Le choix de collaboration Stellium Financement doit être finalisé.',
  [HabilitationBlockingReasonCode.ActionReserveeConsultant]:
    'Cette action est réservée au consultant.',
  [HabilitationBlockingReasonCode.ActionReserveeBackOffice]:
    'Cette action est réservée au back-office.',
  [HabilitationBlockingReasonCode.EtapeNonApplicable]:
    "Cette étape n'est pas applicable à ce parcours.",
  [HabilitationBlockingReasonCode.ConsultantNonAffilie]:
    'Le consultant doit être affilié.',
  [HabilitationBlockingReasonCode.FormationImmoNonInitiee]:
    'La formation immobilier doit être initiée.',
  [HabilitationBlockingReasonCode.FormationImmoNonValidee]:
    'La formation immobilier doit être validée.',
  [HabilitationBlockingReasonCode.FormationHabilitanteAlpsiNonInitiee]:
    'La formation habilitante ALPSI doit être initiée.',
  [HabilitationBlockingReasonCode.FormationHabilitanteAlpsiNonValidee]:
    'La formation habilitante ALPSI doit être validée.',
  [HabilitationBlockingReasonCode.FormationHabilitanteAssuranceNonInitiee]:
    'La formation habilitante assurance doit être initiée.',
  [HabilitationBlockingReasonCode.FormationHabilitanteAssuranceNonValidee]:
    'La formation habilitante assurance doit être validée.',
  [HabilitationBlockingReasonCode.FormationHabilitanteFinancementNonInitiee]:
    'La formation habilitante financement doit être initiée.',
  [HabilitationBlockingReasonCode.FormationHabilitanteFinancementNonValidee]:
    'La formation habilitante financement doit être validée.',
  [HabilitationBlockingReasonCode.FormationHabilitanteCifNonInitiee]:
    'La formation habilitante CIF doit être initiée.',
  [HabilitationBlockingReasonCode.FormationHabilitanteCifNonValidee]:
    'La formation habilitante CIF doit être validée.',
  [HabilitationBlockingReasonCode.CotisationCciNonPayee]:
    'La cotisation CCI doit être déclarée comme payée.',
  [HabilitationBlockingReasonCode.CerfaCciNonGenere]: 'Le Cerfa CCI doit être généré.',
  [HabilitationBlockingReasonCode.DossierCciNonConstitue]:
    'Le dossier CCI doit être constitué.',
  [HabilitationBlockingReasonCode.RetourCciNonRecu]:
    'Le retour de la CCI doit être reçu.',
  [HabilitationBlockingReasonCode.InformationsAttestationCollaborateurNonRenseignees]:
    "Les informations de l'attestation collaborateur doivent être renseignées.",
  [HabilitationBlockingReasonCode.AttestationCollaborateurNonRecuperee]:
    "L'attestation collaborateur doit être récupérée.",
  [HabilitationBlockingReasonCode.AttestationCollaborateurNonGeneree]:
    "L'attestation collaborateur doit être générée.",
  [HabilitationBlockingReasonCode.AttestationCollaborateurNonDeposee]:
    "L'attestation collaborateur doit être déposée.",
  [HabilitationBlockingReasonCode.AttestationCollaborateurNonTransmise]:
    "L'attestation collaborateur doit être transmise.",
  [HabilitationBlockingReasonCode.ConsultantNonHabiliteIob]:
    'Le consultant doit disposer d’une habilitation IOB active.',
  [HabilitationBlockingReasonCode.ConsultantNonHabiliteImmo]:
    'Le consultant doit disposer d’une habilitation immobilier active.',
  [HabilitationBlockingReasonCode.AdhesionAssociationCifNonDemandee]:
    "La demande d'adhésion à l'association CIF doit être déclarée.",
  [HabilitationBlockingReasonCode.AdhesionAssociationCifNonValidee]:
    "L'adhésion à l'association CIF doit être validée.",
  [HabilitationBlockingReasonCode.DemandeSuppressionMandatAlpsiNonRealisee]:
    'La demande de suppression du mandat ALPSI doit être réalisée.',
  [HabilitationBlockingReasonCode.DemandeSuppressionMandatAlpsiOriasNonRealisee]:
    'La demande de suppression du mandat ALPSI auprès de l’ORIAS doit être réalisée.',
  [HabilitationBlockingReasonCode.PriseEnCompteSuppressionMandatAlpsiNonNotifiee]:
    'La prise en compte de la suppression doit être notifiée au consultant.',
  [HabilitationBlockingReasonCode.SuppressionMandatAlpsiNonConfirmee]:
    'La suppression effective du mandat ALPSI doit être confirmée.',
  [HabilitationBlockingReasonCode.SuppressionMandatAlpsiNonNotifiee]:
    'La suppression du mandat ALPSI doit être notifiée au consultant.',
  [HabilitationBlockingReasonCode.InscriptionOriasCifNonRealisee]:
    "L'inscription ORIAS CIF doit être déclarée.",
  [HabilitationBlockingReasonCode.RetourOriasCifNonRecu]:
    'Le retour ORIAS CIF doit être déclaré.',
  [HabilitationBlockingReasonCode.ConventionDistributionCifNonGeneree]:
    'La convention CIF doit être générée.',
  [HabilitationBlockingReasonCode.ConventionDistributionCifNonTransmise]:
    'La convention CIF doit être transmise au back-office.',
  [HabilitationBlockingReasonCode.ConventionDistributionCifNonValidee]:
    'La convention CIF doit être contrôlée.',
  [HabilitationBlockingReasonCode.ConventionDistributionCifNonEnvoyeeEnSignature]:
    "L'envoi de la convention CIF en signature doit être confirmé.",
  [HabilitationBlockingReasonCode.ConventionDistributionCifNonSignee]:
    'La signature de la convention CIF doit être confirmée.',
  [HabilitationBlockingReasonCode.DemandeAlpsiNonTransmise]:
    'La demande ALPSI doit être transmise.',
  [HabilitationBlockingReasonCode.MandatAlpsiNonGenere]:
    'Le mandat ALPSI doit être généré.',
  [HabilitationBlockingReasonCode.MandatAlpsiNonEnvoyeEnSignature]:
    'L’envoi du mandat ALPSI en signature doit être confirmé.',
  [HabilitationBlockingReasonCode.MandatAlpsiNonSigne]:
    'La signature du mandat ALPSI doit être confirmée.',
  [HabilitationBlockingReasonCode.MandantAlpsiNonDeclareOrias]:
    'La déclaration du mandant à l’ORIAS doit être confirmée.',
  [HabilitationBlockingReasonCode.DossierOriasAlpsiNonFinalise]:
    'La finalisation du dossier ORIAS doit être confirmée.',
  [HabilitationBlockingReasonCode.CarteDemarchageFinancementNonGeneree]:
    'La génération de la carte de démarchage Financement doit être confirmée.',
  [HabilitationBlockingReasonCode.CarteDemarchageAlpsiNonGeneree]:
    'La génération de la carte de démarchage ALPSI doit être confirmée.',
};

function isHabilitationActionCompletedLocally(
  action: ActionParcoursHabilitation,
  etapes: HabilitationParcoursEtape[],
  misesAJour: MiseAJourParcours[]
): boolean {
  const miseAJour = misesAJour.find(candidate => candidate.action === action);
  if (miseAJour) return miseAJour.terminee;

  const sousEtape = etapes
    .flatMap(etape => etape.sousEtapes)
    .find(candidate => candidate.type === action);
  return sousEtape?.status === 'terminee';
}

function areActionPrerequisitesSatisfiedLocally(
  prerequisiteActions: ActionParcoursHabilitation[] | undefined,
  etapes: HabilitationParcoursEtape[],
  misesAJour: MiseAJourParcours[]
): boolean {
  return (prerequisiteActions ?? []).every(action =>
    isHabilitationActionCompletedLocally(action, etapes, misesAJour)
  );
}

export function isHabilitationParcoursSubmittableLocally(
  parcours: HabilitationParcours,
  misesAJour: MiseAJourParcours[]
): boolean {
  if (parcours.submitPrerequisiteActions === undefined)
    return parcours.canSubmit === true;

  return (
    parcours.submitExternalRequirementsSatisfied === true &&
    areActionPrerequisitesSatisfiedLocally(
      parcours.submitPrerequisiteActions,
      parcours.etapesTousActeurs ?? parcours.etapes,
      misesAJour
    )
  );
}

export function isHabilitationStepAvailableLocally(
  etape: HabilitationParcoursEtape,
  etapes: HabilitationParcoursEtape[],
  misesAJour: MiseAJourParcours[]
): boolean {
  return (
    etape.externalRequirementsSatisfied !== false &&
    (etape.prerequisiteActions?.length ?? 0) > 0 &&
    areActionPrerequisitesSatisfiedLocally(etape.prerequisiteActions, etapes, misesAJour)
  );
}

export function isHabilitationActionAvailableLocally(
  etape: HabilitationParcoursEtape,
  sousEtape: HabilitationParcoursEtape['sousEtapes'][number],
  etapes: HabilitationParcoursEtape[],
  misesAJour: MiseAJourParcours[]
): boolean {
  if (isHabilitationActionCompletedLocally(sousEtape.type, etapes, misesAJour)) {
    return true;
  }

  if (sousEtape.prerequisiteActions === undefined) return sousEtape.canExecute === true;

  return (
    etape.externalRequirementsSatisfied !== false &&
    sousEtape.externalRequirementsSatisfied !== false &&
    areActionPrerequisitesSatisfiedLocally(
      sousEtape.prerequisiteActions,
      etapes,
      misesAJour
    )
  );
}

export function clearDependentHabilitationUpdates(
  etapes: HabilitationParcoursEtape[],
  misesAJour: MiseAJourParcours[],
  actionDecochee: ActionParcoursHabilitation
): MiseAJourParcours[] {
  const actionsDependantes = new Set<ActionParcoursHabilitation>();
  const sourceAction = etapes
    .flatMap(etape => etape.sousEtapes)
    .find(sousEtape => sousEtape.type === actionDecochee);
  const hasServerDependencyProjection = etapes.some(etape =>
    etape.sousEtapes.some(sousEtape => sousEtape.dependentActions !== undefined)
  );

  if (hasServerDependencyProjection) {
    sourceAction?.dependentActions?.forEach(action => actionsDependantes.add(action));
  } else {
    const actionsAParcourir = [actionDecochee];
    while (actionsAParcourir.length > 0) {
      const action = actionsAParcourir.pop()!;
      for (const sousEtape of etapes.flatMap(etape => etape.sousEtapes)) {
        if (
          sousEtape.prerequisiteActions?.includes(action) &&
          actionsDependantes.add(sousEtape.type)
        ) {
          actionsAParcourir.push(sousEtape.type);
        }
      }
    }
  }

  if (actionsDependantes.size === 0) return misesAJour;

  const misesAJourCascadees = misesAJour.map(miseAJour => {
    return actionsDependantes.has(miseAJour.action) && miseAJour.terminee
      ? { ...miseAJour, terminee: false }
      : miseAJour;
  });
  const actionsDejaMisesAJour = new Set(
    misesAJourCascadees.map(miseAJour => miseAJour.action)
  );

  for (const actionDependante of actionsDependantes)
    if (
      !actionsDejaMisesAJour.has(actionDependante) &&
      isHabilitationActionCompletedLocally(actionDependante, etapes, misesAJour)
    )
      misesAJourCascadees.push({ action: actionDependante, terminee: false });

  return misesAJourCascadees;
}

export function getHabilitationStepPresentation(
  type: HabilitationType,
  code: HabilitationStepCode
): HabilitationStepPresentation {
  const presentation = COMMON_STEP_PRESENTATION[code];
  if (!presentation) {
    throw new Error("L'étape est inconnue du front: " + String(code));
  }

  return {
    ...presentation,
    ...STEP_OVERRIDES[type]?.[code],
  };
}

export function getHabilitationStepPresentationByCode(
  type: HabilitationType,
  code: CodeEtapeParcoursHabilitation
): HabilitationStepPresentation {
  const entry = (
    Object.entries(COMMON_STEP_PRESENTATION) as [
      HabilitationStepCode,
      HabilitationStepPresentation,
    ][]
  ).find(([, presentation]) => presentation.code === code);
  if (!entry) {
    throw new Error("L'étape est inconnue du front: " + code);
  }

  return {
    ...entry[1],
    ...STEP_OVERRIDES[type]?.[entry[0]],
  };
}

export function toGraphQlHabilitationActionCode(
  code: ActionParcoursHabilitation
): HabilitationActionCode | undefined {
  for (const actionCode of Object.keys(
    HABILITATION_ACTION_PRESENTATION
  ) as HabilitationActionCode[]) {
    if (HABILITATION_ACTION_PRESENTATION[actionCode].code === code) {
      return actionCode;
    }
  }

  return undefined;
}
